// src/lib/dataStore.js — the site's generated data, loaded from S3.
//
// The precomputed JSON (playerIndex, champHistory, …) used to ride in the bundle as
// code-split import()s, so every data refresh was a git commit (~14 MB) and two
// Netlify builds. It now lives on the asset bucket, published by the refresh box
// (ops/publish.mjs): content-addressed gzipped files, plus one small manifest per
// channel naming the current build.
//
// A page reads its manifest ONCE and resolves every file through it, so files that
// must agree (champHistory and its index-aligned champReignMeta) always come from
// the same build, even when a refresh lands while the tab is open.
//
// Channel: production on wiznaib.us, staging everywhere else (the staging deploy,
// deploy previews, localhost), so a data-shape change can be tried on staging before
// production code has to understand it. Override with ?data=production|staging, or
// ?data=local to read freshly built files served by `node tools/serveData.mjs`.

import { DATA_BASE } from './assetUrls.js';
import { unitFile } from './unitHistory.js';

const LOCAL_BASE = 'http://127.0.0.1:8090/';

function pickChannel() {
  let loc;
  try { loc = window.location; } catch (e) { return 'staging'; }
  const q = new URLSearchParams(loc.search).get('data');
  if (q === 'production' || q === 'staging' || q === 'local') return q;
  return loc.hostname === 'wiznaib.us' || loc.hostname === 'www.wiznaib.us' ? 'production' : 'staging';
}

export const DATA_CHANNEL = pickChannel();
const BASE = DATA_CHANNEL === 'local' ? LOCAL_BASE : DATA_BASE;

// One retry: a dropped connection shouldn't blank a page that would load on a second try.
async function getJson(url, init) {
  let lastErr;
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      const r = await fetch(url, init);
      if (!r.ok) throw new Error(`HTTP ${r.status} for ${url}`);
      return await r.json();
    } catch (e) { lastErr = e; }
  }
  throw lastErr;
}

let manifest = null;
function getManifest() {
  if (!manifest) {
    // no-cache = revalidate on every page load, so a refresh shows up without a redeploy.
    manifest = getJson(`${BASE}${DATA_CHANNEL}.json`, { cache: 'no-cache' });
    manifest.catch(() => { manifest = null; });   // a later call retries instead of inheriting the failure
  }
  return manifest;
}

const loaded = new Map();

// loadData('playerIndex.json') → the parsed file, fetched at most once per page load.
export function loadData(name) {
  if (!loaded.has(name)) {
    const p = getManifest().then((m) => {
      const key = m.files[name];
      if (!key) throw new Error(`${name} is not in the ${DATA_CHANNEL} data manifest`);
      return loadFile(key);
    });
    p.catch(() => loaded.delete(name));
    loaded.set(name, p);
  }
  return loaded.get(name);
}

// A file by its manifest key. Keys are content hashes, so a key always means the same
// bytes, and loadData() and a reader of a newer manifest share one download.
const files = new Map();
export function loadFile(key) {
  if (!files.has(key)) {
    const p = getJson(BASE + key);
    p.catch(() => files.delete(key));
    files.set(key, p);
  }
  return files.get(key);
}

// Which build the page is showing: when it was made and the newest tournament it covers
// (for the "Stats updated" footer). null fields = a manifest that predates them.
export function dataInfo() {
  return getManifest().then((m) => ({ built: m.built || null, latestId: m.latestId || null }));
}

// The newest manifest, read fresh on every call (the page's own is read once, above).
// For something that outlives the page's build: the bracket page's goal widget
// re-checks it while a tab stays open. null if it can't be read.
export async function latestManifest() {
  try { return await getJson(`${BASE}${DATA_CHANNEL}.json`, { cache: 'no-cache' }); } catch { return null; }
}

// A player's champion-loadout file (≥50-title players), or null for everyone else.
// Memoized like the import() it replaced: the history tab and the Onion Knight
// quest both ask for the same player's file on one profile.
const details = new Map();
export function champDetailsFor(player) {
  if (!details.has(player)) {
    const p = loadData('champDetails/index.json').then((index) => (index[player] ? getJson(BASE + index[player]) : null));
    p.catch(() => details.delete(player));
    details.set(player, p);
  }
  return details.get(player);
}

// Every unit a player fielded (tools/buildUnitHistory.mjs), by canonKey, or null if they
// have none. The index maps each key to its file's content hash.
const unitFiles = new Map();
export function unitHistoryFor(key) {
  if (!unitFiles.has(key)) {
    const p = loadData('units/index.json').then((index) => (index[key] ? loadFile(`f/units/${unitFile(key)}.${index[key]}.json`) : null));
    p.catch(() => unitFiles.delete(key));
    unitFiles.set(key, p);
  }
  return unitFiles.get(key);
}
